// Thin typed fetch client. Every resource module builds on `request()`.
// Kept deliberately small so swapping to an OpenAPI-generated client later is
// a mechanical replacement of the per-resource modules, not this core.

import type { ErrorResponse } from '@seencast/shared';
import { getActiveOrgId } from './org-store.ts';

// In dev + tests the base is relative (`/v1`) so MSW intercepts. In production
// set VITE_API_BASE to the API worker origin (e.g. https://api.example.com/v1).
export const API_BASE: string =
  (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, '') ?? '/v1';

/** Error thrown for any non-2xx response, carrying the parsed error envelope. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
  get isAuth() {
    return this.status === 401;
  }
  get isConflict() {
    return this.status === 409;
  }
  get isValidation() {
    return this.status === 422;
  }
}

type Query = Record<string, string | number | boolean | undefined | null>;

/**
 * Cursor-paginated list response (docs/03 §Conventions). The API wraps every
 * list in this envelope; the per-resource `list*()` helpers unwrap `.items`
 * (Phase 1 loads a single page — the seed/pilot fleets are small).
 */
export interface ListEnvelope<T> {
  items: T[];
  next_cursor: string | null;
}

function buildUrl(path: string, query?: Query): string {
  const url = `${API_BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Query;
  signal?: AbortSignal;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal } = opts;
  // Tenancy is stateless: the active org is named on every request (docs/03 —
  // "org comes from session/token, never from the request" is satisfied by the
  // API validating this header against membership). Omitted when no org is
  // selected (login, superadmin) — the API infers it for single-org users.
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const orgId = getActiveOrgId();
  if (orgId) headers['X-Org-Id'] = orgId;
  const res = await fetch(buildUrl(path, query), {
    method,
    credentials: 'include', // session cookie (CMS principal)
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const data: unknown = text ? JSON.parse(text) : undefined;

  if (!res.ok) {
    const env = data as ErrorResponse | undefined;
    throw new ApiError(
      res.status,
      env?.error?.code ?? 'unknown',
      env?.error?.message ?? res.statusText ?? 'Request failed',
    );
  }
  return data as T;
}

/** Raw presigned upload (R2 PUT). Not JSON — returns nothing on success. */
export async function uploadToUrl(
  uploadUrl: string,
  file: Blob,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  // XHR (not fetch) because we need upload progress events for the UI.
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    // The dev upload target is the API Worker itself (session-cookie auth). Cross-origin
    // (deployed dev: *.pages.dev → *.workers.dev) the cookie only rides along with
    // credentials enabled; harmless for same-origin dev. A presigned R2 URL (carrying an
    // AWS signature) authenticates via that signature and must NOT send credentials, or
    // R2's CORS rejects the request — so scope withCredentials to the non-signed path.
    if (!/[?&]X-Amz-/.test(uploadUrl)) xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new ApiError(xhr.status, 'upload_failed', 'Upload failed'));
    xhr.onerror = () => reject(new ApiError(0, 'network', 'Network error during upload'));
    xhr.onabort = () => reject(new ApiError(0, 'aborted', 'Upload aborted'));
    if (signal) {
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }
    xhr.send(file);
  });
}
